/**
 * Dynamic module color assignment utility
 * 
 * Assigns consistent colors to modules based on their name hash.
 * No hardcoded module names - purely dynamic based on palette.
 */

interface ModuleColorScheme {
  bg: string;
  border: string;
  text: string;
}

const PALETTE: ModuleColorScheme[] = [
  { bg: '#EFF6FF', border: '#3B82F6', text: '#1E40AF' }, // blue
  { bg: '#F0FDF4', border: '#22C55E', text: '#166534' }, // green
  { bg: '#FDF4FF', border: '#A855F7', text: '#6B21A8' }, // purple
  { bg: '#FFFBEB', border: '#F59E0B', text: '#92400E' }, // amber
  { bg: '#FEF2F2', border: '#EF4444', text: '#991B1B' }, // red
  { bg: '#ECFEFF', border: '#06B6D4', text: '#155E75' }, // cyan
  { bg: '#FDF2F8', border: '#EC4899', text: '#9F1239' }, // pink
  { bg: '#F5F3FF', border: '#8B5CF6', text: '#5B21B6' }, // violet
  { bg: '#FEF3C7', border: '#F59E0B', text: '#78350F' }, // yellow
  { bg: '#DBEAFE', border: '#2563EB', text: '#1E3A8A' }, // indigo
];

/**
 * Hash a string to a consistent number
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Get color scheme for a module name
 * 
 * @param moduleName - Name of the module (e.g., "Sales", "Inventory", "Core")
 * @returns Color scheme with background, border, and text colors
 */
export function getModuleColors(moduleName: string): ModuleColorScheme {
  const index = hashString(moduleName) % PALETTE.length;
  return PALETTE[index];
}

/**
 * Get all available module colors
 * Useful for legend or color previews
 */
export function getAllModuleColors(): ModuleColorScheme[] {
  return [...PALETTE];
}

/**
 * Generate inline style for module badge
 */
export function getModuleBadgeStyle(moduleName: string): React.CSSProperties {
  const colors = getModuleColors(moduleName);
  return {
    backgroundColor: colors.bg,
    borderColor: colors.border,
    color: colors.text,
    borderWidth: '1px',
    borderStyle: 'solid',
  };
}

/**
 * Generate CSS class names for module (for use with CSS modules)
 */
export function getModuleColorClasses(moduleName: string): string {
  const index = hashString(moduleName) % PALETTE.length;
  return `module-color-${index}`;
}

